import { useEffect, useState } from "react";

import i18next from "i18next";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { EPA, PDC, PRICE_CHANGED_ERROR_CODES } from "../../constants";

import { checkInAccessToPaymentStatus, getSearchIdForOrder } from "../../utils";

import { checkPrice, getOrder, getPaymentSystems, orderCancel, ticketing, updatePrice } from "../../protocols";

import { setShowSearch } from "../../store/appConfigsSlice";
import { setAvailablePaymentMethods, setOrderData, setPaymentMethod } from "../../store/orderSlice";

import { withTranslation } from "react-i18next";
import {
  ContainerComponent,
  DCPaymentModalComponent,
  DotsLoadingComponent,
  OrderComponent,
  PriceChangedModalComponent,
  VisaPaymentModalComponent,
} from "../../components";
import { LoadingBlock } from "./style";
import { markPaymentStarted, usePaymentStatusWatch } from "../../hooks";

const OrderPage = () => {
  const { t, language } = i18next;

  const { isLoad, paymentMethod, availablePaymentMethods, orderData } = useSelector(state => state.order);
  const searchParams = useSelector(state => state.searchParams);

  const { orderId, sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [responseError, setResponseError] = useState(null);
  const [isChangedStatus, setIsChangedStatus] = useState(false);
  // сообщение над суммой заказа: цены доп. услуг изменились, оплату нужно повторить по новой сумме
  const [optionsNotice, setOptionsNotice] = useState(null);
  const [dcPaymentModal, setDcPaymentModal] = useState(null);
  const [visaPaymentModal, setVisaPaymentModal] = useState(null);
  // «Цена изменилась»: { old_price, new_price, currency, moved_again?, error_desc? } — null, пока бек не сообщил об изменении
  const [priceChanged, setPriceChanged] = useState(null);

  /**
   * Бек сверил оригинальную авиа-цену с поставщиком и сообщил об изменении (check-price при входе,
   * либо ошибка 35/58 в ответе на оплату) — показываем модалку с тем, что показал бек.
   */
  const openPriceChanged = (data, extra = {}) => {
    const check = data?.price_check || data;

    setPriceChanged({
      old_price: check?.old_price ?? data?.old_price,
      new_price: check?.new_price ?? data?.new_price,
      currency: data?.currency || check?.partner_currency || orderData?.currency,
      ...extra,
    });
  };

  const isPriceChangedResponse = data =>
    !!data && (PRICE_CHANGED_ERROR_CODES.includes(data.error_code) || data.is_price_changed === true);

  /** При входе на страницу оплаты: если цена уже уехала — модалка всплывает сразу, до нажатия «Оплатить». */
  const checkPriceOnEnter = order => {
    checkPrice({ order_id: order.order_id, session_id: order.session_id, language })
      .then(response => {
        const data = response?.data;
        if (data && !data.error_code && data.is_price_changed === true) {
          openPriceChanged({ ...data, currency: data.currency || order.currency });
        }
      })
      .catch(error => console.log({ checkPriceError: error }));
  };

  /** Согласен: бек применяет цену, которую сам показал; затем перечитываем заказ с новой суммой. */
  const agreeWithNewPrice = () => {
    const { order_id, session_id } = orderData;

    return updatePrice({ order_id, session_id, language })
      .then(response => {
        const data = response?.data || {};

        if (isPriceChangedResponse(data)) {
          // пока клиент думал, поставщик поменял цену ещё раз — показываем свежую и просим подтвердить снова
          openPriceChanged(data, { moved_again: true });
          return;
        }

        if (data.error_code) {
          setPriceChanged(current => ({ ...current, error_desc: data.error_desc || data.error_key }));
          return;
        }

        setPriceChanged(null);
        getOrderData();
      })
      .catch(error => {
        console.log({ updatePriceError: error });
        setPriceChanged(current => ({ ...current, error_desc: t("price_changed_update_error") }));
      });
  };

  /** Не согласен: обратно в поиск — страница результатов сама запускает поиск заново по searchId. */
  const disagreeWithNewPrice = () => {
    setPriceChanged(null);

    const searchId = getSearchIdForOrder(orderData, searchParams);

    if (searchId) {
      dispatch(setShowSearch(true));
      navigate(`/result/${searchId}`);
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    // dispatch(setShowLoading(true));

    getOrderData();

    dispatch(setShowSearch(false));

    return () => {
      // сброс стора заказа при уходе/смене заказа (раньше экшены вызывались без dispatch и ничего не сбрасывали:
      // на следующем заказе до загрузки висели способ оплаты и комиссия предыдущего); orderData — как в initialState
      dispatch(
        setOrderData({
          orderData: null,
          loadStatus: false,
        })
      );

      dispatch(
        setAvailablePaymentMethods({
          methods: [],
          loadStatus: false,
        })
      );

      dispatch(
        setPaymentMethod({
          method: null,
        })
      );
    };
  }, [location]);

  const sendPayment = callback => {
    const methodData = availablePaymentMethods.find(method => method.id === paymentMethod);

    const { currency, order_id, session_id, total_price, total_price_with_options } = orderData;

    const params = {
      order_id,
      session_id,
      // к оплате идёт билет + выбранные дополнительные услуги (сумму считает бек)
      total_price: {
        [currency]: total_price_with_options ?? total_price,
      },
      payment_systems_id: methodData.id,
      return_url: window.location.href,
      fail_url: window.location.href,
    };

    ticketing(params)
      .then(response => {
        if (response?.data?.additional_options_changed) {
          // денег не взяли: перевозчик изменил цену выбранных доп. услуг (или услуга недоступна) — бек уже пересчитал
          // заказ, показываем новую сумму (вместе с комиссией способа оплаты), клиент платит заново
          setOptionsNotice(i18next.t("additional_options_price_changed"));
          getOrderData(undefined, { keepState: true });
          callback();
          return;
        }

        if (isPriceChangedResponse(response?.data)) {
          // бек не создал платёж: цена у поставщика изменилась — подтверждение, потом оплата заново
          openPriceChanged(response.data);
          callback();
          return;
        }

        if (response?.data?.error_code) {
          console.log({ ticketingError: response.data });
          setResponseError(response.data.error_desc || "Произошла ошибка!");
          callback();
          return;
        }

        // уходим на страницу оплаты: по возврату, пока колбэк банка не пришёл, покажем «проверяем оплату», а не форму
        if (methodData.code !== "BALANCE") markPaymentStarted(order_id);

        if (methodData.code === PDC) {
          const { pay_url, payform } = response.data;

          setDcPaymentModal({
            showPopup: true,
            handleClose: () => setDcPaymentModal(null),
            pay_url,
            payform,
          });

          callback();
        } else if (methodData.code === EPA) {
          setVisaPaymentModal({
            showPopup: true,
            handleClose: () => setVisaPaymentModal(null),
            data: response.data?.pay_url,
          });

          callback();
        } else if (methodData.code === "BALANCE") {
          window.location.reload();
        } else {
          callback();
          window.location.href = response.data?.pay_url;
        }
      })
      .catch(error => {
        console.log({ error });
        setResponseError("Произошла ошибка!");
        callback();
      });
  };

  // keepState — перечитывание после выбора доп. услуг: цену билета у поставщика заново не сверяем и
  // выбранный клиентом способ оплаты не сбрасываем (меняются только суммы и комиссия способов оплаты)
  const getOrderData = (callback = () => {}, { keepState = false } = {}) => {
    getOrder({
      order_id: orderId,
      session_id: sessionId,
      language,
    })
      .then(response => {
        const { booking_status, order_status } = response;
        const isAccessToPayment = checkInAccessToPaymentStatus(booking_status, order_status);

        if (isAccessToPayment) {
          if (!keepState) checkPriceOnEnter(response);

          getPaymentSystems("order", orderId).then(response => {
            dispatch(
              setAvailablePaymentMethods({
                methods: response,
                loadStatus: true,
              })
            );

            const actualMethod =
              (keepState && response.find(method => method.id === paymentMethod)) || _.first(response);

            dispatch(setPaymentMethod({ method: actualMethod ? actualMethod.id : null }));
          });
        } else {
          dispatch(
            setAvailablePaymentMethods({
              loadStatus: true,
            })
          );
        }

        // после выбора доп. услуги страницу не прокручиваем: клиент остаётся на блоке услуг

        if (!keepState) {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }

        dispatch(
          setOrderData({
            orderData: response,
            loadStatus: true,
          })
        );

        callback({ error: false });
      })
      .catch(error => {
        setResponseError({
          error_code: error.response?.data?.error_code,
          error_desc: error.response?.data?.error_desc,
          custom_error: t("do_not_find_order_message"),
        });

        callback({ error: true });
      });
  };

  // после оплаты картой статус обновляется сам: «проверяем оплату» до колбэка банка, затем до конца выписки
  const { checkingPayment } = usePaymentStatusWatch({ order: orderData, onStatusChange: () => getOrderData() });

  const cancelOrder = callback => {
    const { session_id, order_id, key } = orderData;

    const params = {
      order_id,
      session_id,
      key,
      language,
    };

    orderCancel(params).then(() => {
      getOrderData(callback);
    });
  };

  if (!isLoad) {
    return (
      <div>
        <ContainerComponent>
          <LoadingBlock>
            <DotsLoadingComponent color="#01A0D4" />
          </LoadingBlock>
        </ContainerComponent>
      </div>
    );
  }

  return (
    <>
      <OrderComponent
        availablePaymentMethods={availablePaymentMethods}
        sendPayment={sendPayment}
        partnerCode={orderData?.partner_code}
        setIsChangedStatus={setIsChangedStatus}
        isChangedStatus={isChangedStatus}
        cancelOrder={cancelOrder}
        reloadOrder={() => {
          setOptionsNotice(null);
          getOrderData(undefined, { keepState: true });
        }}
        notice={optionsNotice || (checkingPayment ? t("payment_checking_notice") : null)}
      />

      <DCPaymentModalComponent
        showPopup={dcPaymentModal?.showPopup}
        handleClose={dcPaymentModal?.handleClose}
        payUrl={dcPaymentModal?.payment_link}
        payform={dcPaymentModal?.payform}
      />

      <VisaPaymentModalComponent
        showPopup={visaPaymentModal?.showPopup}
        handleClose={visaPaymentModal?.handleClose}
        data={visaPaymentModal?.data}
      />

      <PriceChangedModalComponent data={priceChanged} onAgree={agreeWithNewPrice} onDisagree={disagreeWithNewPrice} />
    </>
  );
};

export default withTranslation()(OrderPage);
